import { IFilter, ISearchCriteria, ISort } from '@dxp/core/models';

const escapeRegExp = (str: string): string =>
    str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const mapFilter = (filter: IFilter) =>
    filter.equals ||
    filter.greaterOrEqualTo ||
    filter.lesserThan ||
    filter.regex
        ? {
              $eq:
                  filter.column === 'Id'
                      ? { $oid: filter.equals }
                      : filter.equals,
              $gte:
                  filter.greaterOrEqualTo instanceof Date
                      ? { $date: filter.greaterOrEqualTo.toISOString() }
                      : filter.greaterOrEqualTo,
              $lt:
                  filter.lesserThan instanceof Date
                      ? { $date: filter.lesserThan.toISOString() }
                      : filter.lesserThan,
              $regex: filter.regex,
          }
        : undefined;

export function buildMongoFilterExpression(
    searchText: string,
    searchableFields: string[],
    filters: IFilter[],
): string {
    if (!searchText && filters.length === 0) return '{}';

    const escapedSearchText = escapeRegExp(searchText);

    const regexExpressions = searchableFields.map(col => ({
        [col]: {
            $regex: `.*${escapedSearchText}.*`,
            $options: 'i',
        },
    }));
    const searchTextExpressions = searchText ? [{ $or: regexExpressions }] : [];

    const filterExpressions = filters.map(filter => ({
        [filter.column === 'Id' ? '_id' : filter.column]: filter.not
            ? { $not: mapFilter(filter) }
            : mapFilter(filter),
    }));

    const result = {
        $and: [...searchTextExpressions, ...filterExpressions],
    };

    return JSON.stringify(result);
}

export function buildMongoSortExpression(sorts: ISort[]): string {
    if (sorts.length === 0) return '{}';

    const sortExpression: Record<string, number> = {};
    sorts.forEach(s => (sortExpression[s.column] = s.descending ? -1 : 1));

    return JSON.stringify(sortExpression);
}

export function buildQueryString(
    searchCriteria: ISearchCriteria,
    searchableFields: string[],
    filters: IFilter[] = [],
    sorts: ISort[] = [],
): string {
    const { filterExpression, skip, limit } = searchCriteria;
    return `filterExpression=${encodeURIComponent(buildMongoFilterExpression(filterExpression, searchableFields, filters))}&sortExpression=${encodeURIComponent(buildMongoSortExpression(sorts))}&skip=${skip}&limit=${limit}`;
}

export function buildArrayContainsQueryString(
    arrayPropName: string,
    values: string[],
): string {
    const objectIds = values.map(v => `ObjectId('${v}')`).join(',');
    const filterExpression = `{${arrayPropName}:{$in:[${objectIds}]}}`;
    return `filterExpression=${encodeURIComponent(filterExpression)}`;
}
